/* =========================================================
   CHAGUAL — Medición del medio embudo y de la búsqueda interna

   Qué es: la capa COMPARTIDA que registra los cuatro pasos comerciales que la
   auditoría 24 encontró ciegos:

     1. ver carrito            (hueco 3 del informe)
     2. quitar del carrito     (hueco 3 del informe)
     3. ver vitrina            (hueco 4 del informe)
     4. cantidad de resultados en la búsqueda interna (hueco 1 y §7 lote M4)

   y que además emite MONEDA y VALOR en la vista de producto (hueco 2), que hoy
   viaja sin ninguno de los dos.

   Qué NO es: una segunda etiqueta, un segundo contenedor ni un cargador manual
   de tracking. No descarga nada, no inyecta scripts y no toca el flujo
   administrado de Shopify. El candado scripts/test-home-tracking-contract.py
   sigue siendo cierto.

   ---------------------------------------------------------
   DÓNDE SE EMITE, Y POR QUÉ AHÍ
   ---------------------------------------------------------
   Destino: `Shopify.analytics.publish`, el canal administrado de Eventos de
   cliente que instala el propio Web Pixels Manager de Shopify. Es el mismo
   destino que ya usa assets/chagual-filter-contract.js, así que no se abre un
   segundo canal: se reutiliza el que existe.

   NO se escribe en `window.dataLayer`. La auditoría 24 §5.6 comprobó que ese
   buzón no tiene lector: en la tienda no existe contenedor de Google Tag
   Manager (`window.google_tag_manager` solo expone la propiedad de GA4 y la
   etiqueta de Google, ninguna clave `GTM-`), y `gtag.js` por sí solo ignora los
   objetos de evento personalizados. Escribir ahí aparentaría medir sin medir.

   Consecuencia honesta: el theme no puede cambiar lo que la integración
   administrada de Google envía a GA4. Lo que sí puede —y hace aquí— es dejar el
   dato completo y coherente en el canal administrado, disponible para el pixel
   personalizado de Chagual que ya está suscrito a Eventos de cliente y para
   cualquier consumidor futuro.

   ---------------------------------------------------------
   POR QUÉ NO DUPLICA NADA
   ---------------------------------------------------------
   * El nombre del evento es `chagual_funnel`, propio y con prefijo. Los eventos
     estándar de Shopify se llaman `cart_viewed`, `product_viewed`,
     `collection_viewed`, `search_submitted`, `product_added_to_cart`… Al no
     compartir nombre con ninguno, los pixeles administrados de Google y de Meta
     —que se suscriben a eventos estándar— no lo recogen y no hay doble conteo.
   * Cada acción se emite UNA sola vez por documento (registro `emitidos`).
   * Quitar del carrito se anota o se publica, nunca las dos cosas (ver abajo).
   * En vitrina se emite una sola vez por carga de documento. Los cambios de
     filtro los mide `chagual_filter`, que ya reporta faceta, valor y cantidad de
     resultados; volver a emitir aquí inflaría la vista de vitrina.

   ---------------------------------------------------------
   COHERENCIA POR VARIANTE
   ---------------------------------------------------------
   Todo el dato de producto viene del bloque JSON que pinta el servidor
   (`#chagual-funnel-config`), construido en Liquid variante por variante. Si un
   evento lleva producto, lleva el identificador, el título, el precio y la
   disponibilidad de ESA MISMA variante. No hay precios calculados aquí ni
   conversiones de moneda: la moneda es la que declara la tienda y el valor es el
   entero de Shopify en centavos dividido por cien, la misma operación que ya
   hace assets/chagual-pdp-atc-price.v1.js.

   La disponibilidad viene de `variant.available` de Shopify y NUNCA de la
   cantidad de inventario: la tienda vende con inventario negativo a propósito.
   ========================================================= */
(function () {
  'use strict';

  var EVENT_NAME = 'chagual_funnel';
  var PENDING_KEY = 'chagual:funnel-pending';
  var CONFIG_ID = 'chagual-funnel-config';

  var emitidos = {};

  // ---------------------------------------------------------------
  // Salida única: el canal administrado. La medición nunca puede romper
  // la tienda, así que todo va envuelto.
  // ---------------------------------------------------------------
  function publish(payload) {
    if (!payload || !payload.action) return;
    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') analytics.publish(EVENT_NAME, payload);
    } catch (error) { /* la medición nunca puede romper la tienda */ }
    // Señal interna del theme, en el mismo formato que `chagual:filter-tracked`.
    try {
      if (typeof window.CustomEvent === 'function') {
        document.dispatchEvent(new CustomEvent('chagual:funnel-tracked', { detail: payload }));
      }
    } catch (error) { /* idem */ }
  }

  // Publica una sola vez por documento. La clave permite distinguir líneas de
  // carrito distintas dentro de la misma acción.
  function publishOnce(key, payload) {
    if (!key || emitidos[key]) return;
    emitidos[key] = true;
    publish(payload);
  }

  // ---------------------------------------------------------------
  // Lectura del bloque que pinta el servidor.
  // ---------------------------------------------------------------
  function readConfig() {
    var node = document.getElementById(CONFIG_ID);
    if (!node) return null;
    try {
      var parsed = JSON.parse(node.textContent || '{}');
      return parsed && parsed.surface ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  // Centavos de Shopify a unidades de la moneda. Es la misma división por cien
  // que usa el theme para mostrar el precio; no es una conversión de moneda.
  function toMajor(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return null;
    return n / 100;
  }

  function currentPath() {
    if (!window.location) return '';
    return window.location.pathname + window.location.search;
  }

  // Campos comunes a todas las acciones.
  function baseFrom(config, action) {
    return {
      action: action,
      surface: config.surface || '',
      currency: config.currency || '',
      url: currentPath()
    };
  }

  // Normaliza una línea o una tarjeta: siempre con variante, precio y
  // disponibilidad de la misma variante.
  function normalizeItem(item) {
    if (!item) return null;
    return {
      product_id: item.product_id || null,
      product_title: item.product_title || '',
      product_url: item.product_url || '',
      variant_id: item.variant_id || null,
      variant_title: item.variant_title || '',
      sku: item.sku || '',
      price: toMajor(item.price_cents),
      price_cents: typeof item.price_cents === 'number' ? item.price_cents : null,
      available: typeof item.available === 'boolean' ? item.available : null,
      quantity: typeof item.quantity === 'number' ? item.quantity : null,
      position: typeof item.position === 'number' ? item.position : null
    };
  }

  function normalizeList(items) {
    if (!items || !items.length) return [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var normalized = normalizeItem(items[i]);
      if (normalized) out.push(normalized);
    }
    return out;
  }

  // ---------------------------------------------------------------
  // Hueco 3a — VER CARRITO.
  // Hoy `/cart` solo emite vista de página: se sabe cuántos agregan, no cuántos
  // abren el carrito. Aquí se registra la apertura con el valor total y las
  // líneas completas.
  // ---------------------------------------------------------------
  function trackCartViewed(config) {
    var payload = baseFrom(config, 'cart_viewed');
    payload.value = toMajor(config.value_cents);
    payload.value_cents = typeof config.value_cents === 'number' ? config.value_cents : null;
    payload.item_count = typeof config.item_count === 'number' ? config.item_count : null;
    payload.items = normalizeList(config.items);
    publishOnce('cart_viewed', payload);
  }

  // ---------------------------------------------------------------
  // Hueco 3b — QUITAR DEL CARRITO.
  //
  // El clic registra solo una intención local. La siguiente vista del carrito
  // debe demostrar que desapareció la línea y cayó la cantidad de esa variante.
  // Sin confirmación, sin almacén o tras un error, no se publica una retirada.
  // ---------------------------------------------------------------
  function buildRemovalPayload(config, lineId, quantity) {
    var items = config.items || [];
    var found = null;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].line) === String(lineId)) { found = items[i]; break; }
    }
    if (!found) return null;
    var payload = baseFrom(config, 'cart_item_removed');
    var removed = normalizeItem(found);
    var removedQuantity = typeof quantity === 'number' && quantity > 0 ? quantity : removed.quantity;
    removed.quantity = removedQuantity;
    payload.items = [removed];
    payload.value_cents = typeof found.line_price_cents === 'number' && found.quantity > 0 ?
      Math.round(found.line_price_cents * removedQuantity / found.quantity) : null;
    payload.value = payload.value_cents === null ? null : toMajor(payload.value_cents);
    payload.line = typeof found.line === 'number' ? found.line : null;
    return payload;
  }

  function stashRemoval(payload, config) {
    if (!payload) return;
    var item = (config.items || []).filter(function (item) { return item.line === payload.line; })[0];
    if (!item || !item.key) return;
    var variantQuantity = (config.items || []).reduce(function (sum, line) {
      return sum + (line.variant_id === item.variant_id ? line.quantity : 0);
    }, 0);
    try {
      if (window.sessionStorage) {
        window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({payload: payload, key: item.key,
          variant_id: item.variant_id, variant_quantity: variantQuantity, created_at: Date.now()}));
      }
    } catch (error) { /* sin confirmación durable: no emitir */ }
  }

  function drainPending(config) {
    var raw = null;
    try {
      if (!window.sessionStorage) return;
      raw = window.sessionStorage.getItem(PENDING_KEY);
      if (raw) window.sessionStorage.removeItem(PENDING_KEY);
    } catch (error) { return; }
    if (!raw) return;
    try {
      var pending = JSON.parse(raw);
      if (config.surface !== 'cart' || !Array.isArray(config.items) || !pending.payload || !pending.key ||
          !pending.created_at || Date.now() - pending.created_at > 60000 || Date.now() < pending.created_at) return;
      if (config.items.some(function (item) { return item.key === pending.key; })) return;
      var quantityNow = config.items.reduce(function (sum, item) {
        return sum + (item.variant_id === pending.variant_id ? item.quantity : 0);
      }, 0);
      if (pending.variant_quantity - quantityNow !== pending.payload.items[0].quantity) return;
      publish(pending.payload);
    } catch (error) { /* dato ilegible: se descarta */ }
  }

  function lineIdFrom(element) {
    var host = element && element.closest ? element.closest('[data-cart-item]') : null;
    return host ? host.getAttribute('data-line-id') : null;
  }

  function bindCartRemoval(config) {
    document.addEventListener('chagual:cart-change-failed', function () {
      try { window.sessionStorage.removeItem(PENDING_KEY); } catch (error) { /* sin almacén */ }
    });
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      // Camino 1: el enlace explícito de quitar la línea.
      var removeBtn = target.closest('[data-remove-item]');
      if (removeBtn) {
        stashRemoval(buildRemovalPayload(config, lineIdFrom(removeBtn), null), config);
        return;
      }

      // Camino 2: bajar la cantidad hasta cero con el botón de menos. Es la
      // misma acción comercial. Se lee el valor ANTES de que el theme lo baje.
      var minus = target.closest('.js-change-quantity');
      if (!minus || minus.getAttribute('data-func') !== 'minus') return;
      var box = minus.parentNode;
      var input = box && box.querySelector ? box.querySelector('input') : null;
      var current = input ? parseInt(input.value, 10) : NaN;
      if (!isFinite(current) || current > 1) return;
      stashRemoval(buildRemovalPayload(config, lineIdFrom(minus), current), config);
    }, true);
  }

  // ---------------------------------------------------------------
  // Hueco 2 — VER PRODUCTO CON MONEDA Y VALOR.
  //
  // La variante vigente se resuelve desde el mismo campo que el formulario envía
  // a `/cart/add` (`[name="id"]`), que es exactamente la fuente con la que
  // agregar al carrito ya construye su evento correcto. Solo se acepta un valor
  // que esté en la lista de variantes de ESTE producto, así una tarjeta de
  // recomendados nunca puede confundir la lectura.
  // ---------------------------------------------------------------
  function variantsById(config) {
    var variants = config.variants || [];
    if (!variants.length) return null;
    var byId = {};
    for (var i = 0; i < variants.length; i++) byId[String(variants[i].variant_id)] = variants[i];
    return byId;
  }

  // Devuelve la variante vigente solo si hay un campo legible que pertenezca a
  // este producto. Un campo de otra ficha —una tarjeta de recomendados— no
  // pertenece a esta tabla y por lo tanto se ignora.
  function readSelectedVariant(byId) {
    var fields = document.querySelectorAll('form[action*="/cart/add"] [name="id"], input[name="id"]');
    for (var f = 0; f < fields.length; f++) {
      var value = fields[f] && fields[f].value ? String(fields[f].value) : '';
      if (value && byId[value]) return byId[value];
    }
    return null;
  }

  function trackProductViewed(config, aceptarPredeterminada) {
    var byId = variantsById(config);
    if (!byId) return false;
    var variant = readSelectedVariant(byId);
    if (!variant) {
      // Sin campo legible todavía. Solo en el último intento se acepta la
      // variante que el servidor declaró seleccionada, para no adelantarse al
      // selector y publicar una variante que no es la que la persona ve.
      if (!aceptarPredeterminada) return false;
      variant = config.default_variant_id ? byId[String(config.default_variant_id)] : null;
      if (!variant) return false;
    }
    var payload = baseFrom(config, 'product_viewed');
    var item = normalizeItem(variant);
    item.product_id = config.product_id || null;
    item.product_title = config.product_title || '';
    item.product_url = config.product_url || '';
    item.quantity = 1;
    payload.vendor = config.vendor || '';
    payload.product_type = config.product_type || '';
    payload.value = item.price;
    payload.value_cents = item.price_cents;
    payload.items = [item];
    publishOnce('product_viewed', payload);
    return true;
  }

  // El campo de variante lo pinta el servidor ya resuelto, así que lo normal es
  // acertar en el primer intento. El reintento acotado cubre el caso en que el
  // selector del theme todavía no haya montado el campo; al agotarse, se acepta
  // la variante que el servidor declaró seleccionada. Emite una sola vez.
  var INTENTOS_MAXIMOS = 8;

  function scheduleProductViewed(config) {
    var intentos = 0;
    function intentar() {
      intentos += 1;
      var ultimo = intentos >= INTENTOS_MAXIMOS;
      if (trackProductViewed(config, ultimo)) return;
      if (ultimo) return;
      window.setTimeout(intentar, 250);
    }
    intentar();
  }

  // ---------------------------------------------------------------
  // FICHA DE ESCALA — APERTURA Y CAMBIO DE VARIANTE CON MEDIDAS ABIERTAS.
  // El motor visual solo emite una señal interna. Este owner valida que la
  // variante pertenezca al producto y publica por el mismo canal administrado
  // de `chagual_funnel`; no escribe en dataLayer ni instala etiquetas.
  // ---------------------------------------------------------------
  var SCALE_EVENT = 'chagual:scale-measurement';
  var SCALE_ACTIONS = {
    scale_measurements_opened: true,
    scale_variant_changed: true
  };

  function finiteMeasurement(value) {
    var parsed = Number(value);
    return isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function trackScaleMeasurement(config, detail) {
    if (!detail || !SCALE_ACTIONS[detail.action] || typeof detail.interaction_id !== 'string' || !detail.interaction_id) return;
    var byId = variantsById(config);
    var variant = byId && byId[String(detail.variant_id || '')];
    var primary = finiteMeasurement(detail.primary_cm);
    var height = finiteMeasurement(detail.height_cm);
    var sizeLabel = typeof detail.size_label === 'string' ? detail.size_label : '';
    var measureType = detail.measure_type === 'diameter' || detail.measure_type === 'width' ? detail.measure_type : '';
    if (!variant || primary === null || height === null || !sizeLabel || !measureType) return;

    var payload = baseFrom(config, detail.action);
    payload.component = 'scale_card';
    payload.product_id = config.product_id || null;
    payload.product_title = config.product_title || '';
    payload.product_url = config.product_url || '';
    payload.variant_id = variant.variant_id || null;
    payload.variant_title = variant.variant_title || '';
    payload.sku = variant.sku || '';
    payload.size_label = sizeLabel;
    payload.measure_type = measureType;
    payload.primary_cm = primary;
    payload.height_cm = height;
    payload.presentation = typeof detail.presentation === 'string' ? detail.presentation : '';
    payload.illustration_mode = typeof detail.illustration_mode === 'string' ? detail.illustration_mode : '';
    publishOnce('scale_measurement:' + detail.interaction_id, payload);
  }

  function bindScaleMeasurement(config) {
    document.addEventListener(SCALE_EVENT, function (event) {
      trackScaleMeasurement(config, event && event.detail);
    });
  }

  // ---------------------------------------------------------------
  // Hueco 4 — VER VITRINA.
  // Ninguna vitrina registraba hoy que fue vista como vitrina. Las tarjetas
  // vienen del mismo orden que ya alimenta la grilla visible y el ItemList
  // publicado, así que la lista medida no puede descuadrarse de la lista vista.
  // ---------------------------------------------------------------
  function trackCollectionViewed(config) {
    var payload = baseFrom(config, 'collection_viewed');
    payload.collection_handle = config.collection_handle || '';
    payload.collection_title = config.collection_title || '';
    payload.collection_url = config.collection_url || '';
    payload.list_total = typeof config.list_total === 'number' ? config.list_total : null;
    payload.page = typeof config.page === 'number' ? config.page : null;
    payload.items = normalizeList(config.items);
    payload.items_reported = payload.items.length;
    publishOnce('collection_viewed', payload);
  }

  // ---------------------------------------------------------------
  // Hueco 1 / lote M4 — CANTIDAD DE RESULTADOS EN LA BÚSQUEDA INTERNA.
  // El término ya se registra tanto si encuentra como si no; lo que falta es el
  // conteo, sin el cual no se puede separar la búsqueda exitosa de la vacía sin
  // repetirla a mano. `results_count` es el total de Shopify, no el de la página.
  // ---------------------------------------------------------------
  function trackSearchResults(config) {
    if (config.performed === false) return;
    var payload = baseFrom(config, 'search_results');
    payload.search_term = config.terms || '';
    payload.results_count = typeof config.results_count === 'number' ? config.results_count : null;
    payload.zero_results = payload.results_count === 0;
    payload.page = typeof config.page === 'number' ? config.page : null;
    payload.items = normalizeList(config.items);
    payload.items_reported = payload.items.length;
    publishOnce('search_results', payload);
  }

  // ---------------------------------------------------------------
  function init() {
    // Seguro duro contra el doble conteo: si por cualquier razón este archivo
    // llegara dos veces al mismo documento, la segunda copia no emite nada. Un
    // evento emitido dos veces infla las cifras y es peor que no tenerlo.
    if (window.__chagualFunnelTracking) return;
    window.__chagualFunnelTracking = true;

    var config = readConfig();
    if (!config) return;

    // Reconciliar la intención con el carrito confirmado; otras superficies
    // descartan el pendiente porque no prueban el resultado de la mutación.
    drainPending(config);

    if (config.surface === 'cart') {
      trackCartViewed(config);
      bindCartRemoval(config);
      return;
    }
    if (config.surface === 'product') {
      bindScaleMeasurement(config);
      scheduleProductViewed(config);
      return;
    }
    if (config.surface === 'collection') {
      trackCollectionViewed(config);
      return;
    }
    if (config.surface === 'search') {
      trackSearchResults(config);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
